import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, EDIT, DESC, ASC } from "const"
import {
    MtDriveFile,
    QidModel,
    QidModelSafetyFunction,
    QidModelOption,
} from "server/models"
import { hasAccess, StateType } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_QID_PRODUCT,
    StoreType,
    LoadQidDatapoints,
    PrepareDatapoints,
    SortQidModels,
    setQidDatapointList,
    setQidDatapointReferenceList,
    setQidDatapointReloadList,
    addModal,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Loader,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    Empty,
    Button,
} from "client/components"

import List from "./list"
import Edit from "./edit"


const FailureRates = () => {
    const product = useSelector((state: StoreType) => state.qidProduct.productView)
    const documentList = useSelector((state: StoreType) => state.qidProductDocument.documentList)
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const reloadList = useSelector((state: StoreType) => state.qidDatapoint.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [modelIds, setModelIds] = useState<number[]>([])
    const [model, setModel] = useState<QidModel>(null)
    const [safetyFunction, setSafetyFunction] = useState<QidModelSafetyFunction>(null)
    const [option, setOption] = useState<QidModelOption>(null)

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        const documents: MtDriveFile[] = []
        Object.values(documentList).forEach((doc) => {
            if (doc.file && doc.file.datapointReference) {
                documents.push(doc.file)
            }
        })

        documents.sort((a, b) => sort(a, b, "name", ASC))
        dispatch(setQidDatapointReferenceList(documents))
    }, [documentList])

    useEffect(() => {
        const sortedIds = SortQidModels(Object.values(modelList), "name", DESC, {})
        setModelIds(sortedIds)
    }, [modelList])

    useEffect(() => {
        if (!reloadList) return
        if (!product || !product.id) return

        setLoadState(PENDING)
        // use "undefined" to load all datapoints on lower levels
        LoadQidDatapoints({
            productId: product.id,
            modelId: model ? model.id : undefined,
            safetyFunctionId: safetyFunction ? safetyFunction.id : undefined,
            optionId: option ? option.id : undefined,
        }).then((datapoints) => {
            const datapointList = PrepareDatapoints(datapoints)
            dispatch(setQidDatapointList(datapointList))
            setLoadState(IDLE)
        })
    }, [product, reloadList])

    const reload = () => {
        dispatch(setQidDatapointReloadList(false))
        setTimeout(() => {
            dispatch(setQidDatapointReloadList(true))
        }, 0)
    }

    useEffect(() => {
        setSafetyFunction(null)
        setOption(null)
        reload()
    }, [model])

    useEffect(() => {
        setOption(null)
        reload()
    }, [safetyFunction])

    useEffect(() => {
        reload()
    }, [option])

    const productEditAccess = hasAccess(PRM_QID_PRODUCT, EDIT)

    return (
        <Container>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }

                <PageForm>
                    <Row>
                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.model}</Label>
                                <SelectDropdown title={model ? model.name : lang.selectModel}>
                                    <SelectDropdownItem onClick={() => setModel(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        modelIds.map((id) => (
                                            <SelectDropdownItem key={id} onClick={() => setModel(modelList[id])}>
                                                {modelList[id] && modelList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>

                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.safetyFunction}</Label>
                                <SelectDropdown title={safetyFunction ? safetyFunction.name : lang.selectSafetyFunction}>
                                    <SelectDropdownItem onClick={() => setSafetyFunction(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        model && model.safetyFunctions.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => setSafetyFunction(row)}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>

                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.option}</Label>
                                <SelectDropdown title={option ? option.name : lang.selectOption}>
                                    <SelectDropdownItem onClick={() => setOption(null)}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        safetyFunction && safetyFunction.options.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => setOption(row)}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                    </Row>

                    {
                        productEditAccess && (
                            <Button
                                variant="success"
                                onClick={() => {
                                    dispatch(addModal({
                                        key: "addDatapoint",
                                        content: <Edit
                                            modelId={model ? model.id : null}
                                            safetyFunctionId={safetyFunction ? safetyFunction.id : null}
                                            optionId={option ? option.id : null}
                                            action="add"
                                        />,
                                    }))
                                }}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </PageForm>

                <List />
            </PageContent>
        </Container>
    )
}
export default FailureRates
